//@ts-nocheck

'use client'

import React, { useContext, useEffect, useId, useMemo, useRef, useState } from 'react'
import { Formik, Form } from 'formik'
import { useTranslations } from 'next-intl'
import { useSearchParams } from 'next/navigation'

import { Heading, Paragraph } from '../../atoms/Typography/Typography.component'
import { Container, Row, Col, SpacerBase } from '../../atoms/GridSystem'
import Icon from '../../atoms/Icon/Icon.component'
import Button from '../../atoms/Button/Button.component'
import Input from '../../atoms/Fields/Input/Input.component'
import Checkbox from '../../atoms/Fields/Checkbox/Checkbox.component'
import Textarea from '../../atoms/Fields/Textarea/Textarea.component'
import Select from '../../atoms/Fields/Select/Select.component'
import type { SelectOption } from '../../atoms/Fields/Select/Select.component'

import type { ComposableFormField, ComposableFormTitle, SubmitAction } from '../../../models/organisms/ComposableForm'

import * as S from './ComposableForm.styles'
import { SectionThemeContext } from '../../../contexts/SectionTheme.context'
import {
  D365_BUNDLE_URL,
  D365_FORM_API_URL,
} from './ComposableForm.constants'
import {
  type CrmFieldMapping,
  buildInitialValues,
  isFieldHidden,
  buildValidationSchema,
  parseSelectOptions,
  buildCrmMappings,
} from './ComposableForm.utils'

type D365FormCapture = {
  serializeForm: (
    form: HTMLFormElement,
    mappings: CrmFieldMapping[]
  ) => { SerializedForm: { build: () => unknown } }
  submitForm: (
    config: { FormId: string; FormApiUrl: string },
    payload: unknown
  ) => Promise<void>
}

type ComposableFormProps = {
  sectionId?: string | null
  type?: string
  colorScheme?: string
  withBackground?: boolean
  title?: ComposableFormTitle
  description?: string
  crmId?: string
  submitAction?: SubmitAction
  pageId?: number
  successMessage?: string
  submitLabel?: string
  privacyLabel?: string
  fields?: ComposableFormField[]
}

function ComposableForm({
  withBackground,
  title,
  description,
  crmId,
  submitAction,
  pageId,
  successMessage,
  submitLabel,
  privacyLabel,
  fields = [],
}: ComposableFormProps) {
  const t = useTranslations()
  const searchParams = useSearchParams()
  const { selectedColorScheme } = useContext(SectionThemeContext)
  const [isSubmitted, setIsSubmitted] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [submitError, setSubmitError] = useState(false)
  const formTitleId = useId()
  const d365Ref = useRef<D365FormCapture | null>(null)

  useEffect(() => {
    if (submitAction !== 'sendToCrm') return

    const existing = document.querySelector(
      `script[src="${D365_BUNDLE_URL}"]`
    ) as HTMLScriptElement | null

    if (existing) {
      d365Ref.current = (window as any).d365mktformcapture ?? null
      return
    }

    const script = document.createElement('script')
    script.src = D365_BUNDLE_URL
    script.async = true
    script.onload = () => {
      d365Ref.current = (window as any).d365mktformcapture ?? null
    }
    document.head.appendChild(script)
  }, [submitAction])

  const resolvedSubmitLabel = submitLabel || t('composableForm.submitLabel')
  const resolvedSuccessMessage = successMessage || t('composableForm.successMessage')
  const resolvedPrivacyLabel = privacyLabel || t('composableForm.privacyLabel')

  const initialValues = useMemo(
    () => ({ ...buildInitialValues(fields, searchParams), privacy: false }),
    [fields, searchParams]
  )

  const validationSchema = useMemo(
    () => buildValidationSchema(fields, t, searchParams),
    [fields, t, searchParams]
  )

  const submitToStrapi = async (values: Record<string, string | boolean>) => {
    const { privacy, ...formValues } = values
    const cleanValues = Object.fromEntries(
      Object.entries(formValues).filter(
        ([, value]) => typeof value === 'string' && value !== ''
      )
    )

    const response = await fetch('/api/forms/submit', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ pageId, values: cleanValues }),
    })

    if (!response.ok) {
      throw new Error('Failed to submit to Strapi')
    }
  }

  const submitToCrm = async (values: Record<string, string | boolean>) => {
    const d365 = d365Ref.current
    if (!d365) {
      throw new Error('D365 Form Capture script not loaded')
    }

    const formElement = document.querySelector(
      `[data-crm-id="${crmId}"]`
    ) as HTMLFormElement | null

    if (!formElement) {
      throw new Error('Form element not found')
    }

    const mappings = buildCrmMappings(fields, values)
    const serialized = d365.serializeForm(formElement, mappings)
    const payload = serialized.SerializedForm.build()

    await d365.submitForm(
      { FormId: crmId!, FormApiUrl: D365_FORM_API_URL },
      payload
    )
  }

  const handleSubmit = async (values: Record<string, string | boolean>) => {
    setSubmitError(false)
    setIsSubmitting(true)

    try {
      switch (submitAction) {
        case 'sendToCrm':
          await submitToCrm(values)
          break
        case 'sendEmail':
          await submitToStrapi(values)
          break
        case 'none':
        default:
          break
      }

      setIsSubmitted(true)
    } catch {
      setSubmitError(true)
    } finally {
      setIsSubmitting(false)
    }
  }

  if (isSubmitted) {
    return (
      <S.ComposableForm colorScheme={selectedColorScheme} withBackground={withBackground}>
        <Container>
          <S.SuccessMessage aria-live="polite" role="status">
            <S.CheckCircle>
              <Icon name="check" color="white" size="large" aria-hidden />
            </S.CheckCircle>
            <SpacerBase xs={3} />
            <S.FadeSlideUp $delay={0.3}>
              <Heading typo="headingXL" as="h2" content={resolvedSuccessMessage} />
            </S.FadeSlideUp>
          </S.SuccessMessage>
        </Container>
      </S.ComposableForm>
    )
  }

  return (
    <S.ComposableForm colorScheme={selectedColorScheme} withBackground={withBackground}>
      <Container>
        {title ? (
          <>
            <Heading
              id={formTitleId}
              typo={title.typo}
              content={title.content}
              fontPrimary
            />
            <SpacerBase xs={2} />
          </>
        ) : null}

        {description ? (
          <>
            <Paragraph typo="paragraphMD" content={description} />
            <SpacerBase xs={3} />
          </>
        ) : null}

        <Formik
          initialValues={initialValues}
          validationSchema={validationSchema}
          onSubmit={handleSubmit}
        >
          <Form noValidate data-crm-id={crmId} aria-labelledby={title ? formTitleId : undefined}>
            <Row>
              {fields.map((field) => {
                const urlParamValue = field.compileWithUrlParams
                  ? searchParams?.get(field.inputId)
                  : null
                const isDisabled = field.compileWithUrlParams && !!urlParamValue
                const isHidden = isFieldHidden(field, urlParamValue)

                return (
                    <Col
                      key={field.id}
                      xs={12}
                      lg={field.width === 'half' ? 6 : 12}
                    >
                      <S.FieldWrapper $hidden={isHidden}>
                        {field.inputType === 'textarea' ? (
                          <Textarea
                            required={field.requiredField}
                            name={field.inputId}
                            id={field.inputId}
                            label={field.label}
                            placeholder={field.placeholder || undefined}
                            aria-required={field.requiredField}
                            withBackground={withBackground}
                            disabled={isDisabled}
                          />
                        ) : field.inputType === 'select' ? (
                          isHidden ? (
                            <Input
                              key={field.id}
                              name={field.inputId}
                              id={field.inputId}
                              type="hidden"
                              withFeedback={false}
                              uppercase={false}
                              password={false}
                              withBackground={withBackground}
                            />
                          ) : (
                            <Select
                              required={field.requiredField}
                              name={field.inputId}
                              id={field.inputId}
                              label={field.label}
                              options={parseSelectOptions(field.selectOptions, t('composableForm.selectPlaceholder'))}
                              aria-required={field.requiredField}
                              withBackground={withBackground}
                              disabled={isDisabled}
                            />
                          )
                        ) : (
                          isHidden ? (
                            <Input
                              key={field.id}
                              name={field.inputId}
                              id={field.inputId}
                              type="hidden"
                              withFeedback={false}
                              uppercase={false}
                              password={false}
                              withBackground={withBackground}
                            />
                          ) : (
                            <Input
                              required={field.requiredField}
                              name={field.inputId}
                              id={field.inputId}
                              type={field.inputType}
                              label={field.label}
                              placeholder={field.placeholder || undefined}
                              aria-required={field.requiredField}
                              withFeedback={false}
                              uppercase={false}
                              password={false}
                              withBackground={withBackground}
                              disabled={isDisabled}
                            />
                          )
                        )}
                      </S.FieldWrapper>
                    </Col>
                  )
                })
              }
            </Row>

            <SpacerBase xs={1} />

            <Checkbox
              name="privacy"
              id="privacy"
              label={resolvedPrivacyLabel}
              required
              aria-required={true}
              withBackground={withBackground}
            />

            {submitError ? (
              <S.ErrorMessage role="alert">
                <Paragraph typo="paragraphSM" content={t('composableForm.errorSubmit')} />
              </S.ErrorMessage>
            ) : null}

            <S.SubmitWrapper>
              <SpacerBase xs={2} />
              <Button
                type="submit"
                label={resolvedSubmitLabel}
                variant="primary"
                inheritedColorScheme={selectedColorScheme}
                disabled={isSubmitting}
              />
            </S.SubmitWrapper>
          </Form>
        </Formik>
      </Container>
    </S.ComposableForm>
  )
}

export default ComposableForm
